/**
 * The counter panel in the hero: what a customer actually gets.
 *
 * Two earlier versions of this were wrong in the same way — they showed a PICTURE of a
 * till rather than an answer to the question a visitor arrives with.
 *
 * The first was a Three.js model of the hardware: 600 kB, the heaviest thing on the
 * site, for an audience largely on mobile data, and the client did not want it.
 *
 * The second was a flat drawing of a salon's bill — "Cut & blow dry 3,500", a total,
 * a made-up shop name. It looked convincing and said nothing. Worse, it invited the
 * wrong question: whose bill is this, and why am I being shown someone's sales? The
 * one thing a visitor needs on the first screen is what they would be buying, and a
 * receipt for haircuts is not that.
 *
 * So the panel now lists the parts of the counter, in the words a shop owner would use,
 * and it is the CONFIGURATOR that fills it in — pick a printer below and a printer
 * appears here. That is what "the picture above changes" means, and it is now literally
 * true rather than decorative. Nothing here is invented: every line is something the
 * company supplies, and the list changes only because the visitor changed it.
 */

export interface CounterConfig {
  printer: boolean;
  drawer: boolean;
  scanner: boolean;
}

/**
 * The software line is always present because there is no counter without it — the
 * configurator offers a choice of scope, never an option to leave it out.
 */
const SOFTWARE = { name: 'Billing software', note: 'Made for your shop, not a template' };

export function PosTerminal({ config, label }: { config: CounterConfig; label?: string }) {
  const parts = [
    { name: 'Touch screen', note: 'The till your staff use all day', on: true },
    { name: 'Bill printer', note: 'Prints and cuts the bill', on: config.printer },
    { name: 'Cash drawer', note: 'Opens when the bill prints', on: config.drawer },
    { name: 'Barcode scanner', note: 'For shops with many items', on: config.scanner },
  ].filter((part) => part.on);

  return (
    <div className="till-wrap">
      {label ? <p className="figure-label">{label}</p> : null}

      <div className="till">
        <p className="till__title">What you get</p>

        <ul className="till__parts">
          <li className="till__part">
            <span className="till__tick" aria-hidden="true">✓</span>
            <span>
              <span className="till__part-name">{SOFTWARE.name}</span>
              <span className="till__part-note">{SOFTWARE.note}</span>
            </span>
          </li>
          {parts.map((part) => (
            <li key={part.name} className="till__part">
              <span className="till__tick" aria-hidden="true">✓</span>
              <span>
                <span className="till__part-name">{part.name}</span>
                <span className="till__part-note">{part.note}</span>
              </span>
            </li>
          ))}
        </ul>

        <p className="till__foot">Fitted in your shop by our own team.</p>
      </div>
    </div>
  );
}
